import { useEffect, useState } from 'react';
import { formaterIdentite, formaterQuantite, montant, somme } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { Tableau, type Colonne } from './Tableau';
import { BandeauAlerte } from './BandeauAlerte';
import { Modale } from './Modale';
import { Nombre } from './Nombre';

interface Candidat {
  id: string;
  reference: string;
  sens: string;
  quantiteDemandee: string | null;
  montantDemande: string | null;
  montantEstime: string;
  mandat: { reference: string; client: { nom: string; prenom: string } };
  instrument: { libelle: string } | null;
  tranche: { libelle: string } | null;
}

/**
 * Aperçu du panier avant sa constitution : les ordres validés du canal, tous
 * cochés d'emblée. Un ordre décoché reste validé et attend le panier suivant.
 *
 * Le serveur n'emporte que les ordres cochés, et refuse tout si l'un d'eux a
 * changé entre l'aperçu et la génération : le panier ne diffère jamais de ce
 * qui a été vu ici.
 */
export function ApercuPanier({
  canal,
  libelle,
  onFermer,
  onGenere,
}: {
  canal: string;
  libelle: string;
  onFermer: () => void;
  onGenere: () => void;
}) {
  const { donnees, chargement, erreur, recharger } = useRessource<Candidat[]>(
    `/paniers/candidats?canal=${canal}`,
  );
  const candidats = donnees ?? [];
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [occupe, setOccupe] = useState(false);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  // Tout est coché à chaque chargement : par défaut, le panier emporte ce
  // qu'il emportait avant l'aperçu.
  useEffect(() => {
    setSelection(new Set((donnees ?? []).map((o) => o.id)));
  }, [donnees]);

  const choisis = candidats.filter((o) => selection.has(o.id));
  const toutSelectionne = candidats.length > 0 && choisis.length === candidats.length;
  const partiellement = choisis.length > 0 && !toutSelectionne;
  const total = somme(choisis.map((o) => montant(o.montantEstime)));

  function basculer(id: string) {
    const copie = new Set(selection);
    if (copie.has(id)) copie.delete(id);
    else copie.add(id);
    setSelection(copie);
  }

  async function generer() {
    setErreurAction(null);
    setOccupe(true);
    try {
      await api.post('/paniers/generer', { canal, ordreIds: choisis.map((o) => o.id) });
      onGenere();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Constitution impossible.');
      // L'aperçu a pu vieillir : on le relit pour que la sélection suivante
      // porte sur des ordres encore disponibles.
      await recharger();
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Candidat>[] = [
    {
      cle: 'choix',
      entete: (
        <input
          type="checkbox"
          checked={toutSelectionne}
          // L'état indéterminé ne s'exprime pas en JSX : il se pose sur le nœud.
          ref={(noeud) => {
            if (noeud) noeud.indeterminate = partiellement;
          }}
          onChange={() =>
            setSelection(toutSelectionne ? new Set() : new Set(candidats.map((o) => o.id)))
          }
          disabled={candidats.length === 0}
          aria-label="Tout cocher"
        />
      ),
      rendu: (o) => (
        <input
          type="checkbox"
          checked={selection.has(o.id)}
          onChange={() => basculer(o.id)}
          aria-label={`Retenir ${o.reference}`}
        />
      ),
    },
    { cle: 'reference', entete: 'Ordre', num: true, rendu: (o) => o.reference },
    {
      cle: 'mandat',
      entete: 'Mandat',
      rendu: (o) => (
        <>
          {o.mandat.reference} — {formaterIdentite(o.mandat.client.nom, o.mandat.client.prenom)}
        </>
      ),
    },
    {
      cle: 'sens',
      entete: 'Sens',
      rendu: (o) => (
        <span className={o.sens === 'ACHAT' ? 'ordre-achat' : 'ordre-vente'}>{o.sens}</span>
      ),
    },
    {
      cle: 'sousjacent',
      entete: 'Sous-jacent',
      rendu: (o) => o.instrument?.libelle ?? `BDT · ${o.tranche?.libelle ?? '—'}`,
    },
    {
      cle: 'quantite',
      entete: 'Quantité / montant',
      num: true,
      rendu: (o) =>
        o.quantiteDemandee ? (
          formaterQuantite(o.quantiteDemandee)
        ) : (
          <Nombre valeur={o.montantDemande} />
        ),
    },
    {
      cle: 'estime',
      entete: 'Montant estimé',
      num: true,
      rendu: (o) => <Nombre valeur={o.montantEstime} />,
    },
  ];

  return (
    <Modale ouverte onFermer={onFermer} surtitre="Aperçu du panier" titre={libelle} taille="large">
      {(erreur || erreurAction) && (
        <BandeauAlerte nature="refus" titre="Constitution refusée">
          {erreurAction ?? messageErreur(erreur)}
        </BandeauAlerte>
      )}
      <p className="consigne consigne--tete">
        Ordres validés du canal, pas encore placés dans un panier. Décochez ceux qui doivent
        attendre : ils restent validés et seront proposés au panier suivant.
      </p>

      <Tableau
        colonnes={colonnes}
        lignes={candidats}
        cle={(o) => o.id}
        vide={chargement ? 'Chargement…' : 'Aucun ordre validé en attente sur ce canal.'}
        pied={
          candidats.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={6}>
                  <b>
                    {choisis.length} ordre(s) retenu(s) sur {candidats.length}
                  </b>
                </td>
                <td className="num">
                  <Nombre valeur={total} />
                </td>
              </tr>
            </tfoot>
          ) : undefined
        }
      />

      <div className="modale__actions">
        <button type="button" className="bouton bouton--fantome" onClick={onFermer}>
          Annuler
        </button>
        <button
          type="button"
          className="bouton"
          disabled={occupe || choisis.length === 0}
          onClick={() => void generer()}
        >
          {occupe ? 'Constitution…' : `Constituer le panier (${choisis.length})`}
        </button>
      </div>
    </Modale>
  );
}
